import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { JobCandidateBody } from 'src/app/models/job_candidate.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';

export interface JobCandidateFormDialogData {
  job_vacancy_id: number;
}

type UserLinkMode = 'link' | 'create';

@Component({
  selector: 'app-job-vacancy-candidate-form-dialog',
  templateUrl: './job-vacancy-candidate-form-dialog.component.html',
  styleUrl: './job-vacancy-candidate-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
  ],
})
export class JobCandidateFormDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<JobCandidateFormDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  readonly data = inject<JobCandidateFormDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly can_save = signal(false);
  readonly user_link_mode = signal<UserLinkMode>('create');
  readonly linked_users = signal<User[]>([]);
  readonly selected_linked_user = signal<User | null>(null);

  readonly displayLinkedUserForAutocomplete = (user: User | string | null): string => {
    if (user === null || typeof user === 'string') {
      return user ?? '';
    }

    return this.formatUserLabel(user);
  };

  readonly form = this.form_builder.group({
    user_link_mode: ['create' as UserLinkMode, Validators.required],
    user_search: [''],
    user_id: [null as number | null],
    name: ['', Validators.required],
    surnames: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    linkedin_username: [''],
    city: [''],
    sex: [null as 'M' | 'F' | null],
    notes: [''],
  });

  ngOnInit(): void {
    const initial_user_link_mode = this.form.get('user_link_mode')?.value ?? 'create';
    this.user_link_mode.set(initial_user_link_mode);
    this.applyUserLinkModeValidators(initial_user_link_mode);

    this.form
      .get('user_link_mode')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((mode: UserLinkMode | null): void => {
        if (mode === null) {
          return;
        }

        this.user_link_mode.set(mode);
        this.applyUserLinkModeValidators(mode);
      });

    this.form
      .get('user_search')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((search_term: string | User | null) => {
          const search = this.resolveUserSearchQuery(search_term);

          if (search === null || search.length < 2) {
            return of([] as User[]);
          }

          return this.api_service
            .getUsers({
              search,
              per_page: 20,
              scopes: 'candidate',
              with: 'role',
            })
            .pipe(map((response: ArrayResponse<User> | User[]): User[] => (Array.isArray(response) ? response : response.data)));
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe((users: User[]): void => {
        this.linked_users.set(users);
      });

    this.form
      .get('user_search')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((search_term: string | User | null): void => {
        if (search_term !== null && typeof search_term === 'object') {
          this.applyLinkedUserSelection(search_term);
        }
      });

    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((): void => {
      this.refreshCanSave();
    });
  }

  displayLinkedUser(user: User | null): string {
    if (user === null) {
      return '';
    }

    return this.formatUserLabel(user);
  }

  onLinkedUserSelected(event: MatAutocompleteSelectedEvent): void {
    this.applyLinkedUserSelection(event.option.value as User);
  }

  clearLinkedUser(): void {
    this.selected_linked_user.set(null);
    this.form.patchValue({
      user_id: null,
      user_search: '',
    });
    this.refreshCanSave();
  }

  save(): void {
    if (this.loading() || !this.can_save()) {
      return;
    }

    this.loading.set(true);

    const raw = this.form.getRawValue();
    const body: JobCandidateBody = {
      job_vacancy_id: this.data.job_vacancy_id,
      notes: raw.notes || null,
    };

    if (this.user_link_mode() === 'link') {
      body.user_id = raw.user_id;
    } else {
      body.user = {
        name: raw.name ?? '',
        surnames: raw.surnames ?? '',
        email: raw.email ?? '',
        phone: raw.phone || null,
        linkedin_username: raw.linkedin_username || null,
        city: raw.city || null,
        sex: raw.sex,
      };
    }

    this.api_service
      .createJobCandidate(body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.dialog_ref.close(true);
        },
        error: (err: { error?: { error?: string } }): void => {
          this.loading.set(false);
          this.refreshCanSave();
          const message = err?.error?.error ?? 'Error al crear el candidato.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  cancel(): void {
    this.dialog_ref.close(false);
  }

  private applyUserLinkModeValidators(mode: UserLinkMode): void {
    const user_id_control = this.form.get('user_id');
    const name_control = this.form.get('name');
    const surnames_control = this.form.get('surnames');
    const email_control = this.form.get('email');

    if (mode === 'link') {
      user_id_control?.enable({ emitEvent: false });
      user_id_control?.setValidators([Validators.required]);
      name_control?.disable({ emitEvent: false });
      surnames_control?.disable({ emitEvent: false });
      email_control?.disable({ emitEvent: false });
    } else {
      user_id_control?.disable({ emitEvent: false });
      user_id_control?.clearValidators();
      user_id_control?.setValue(null, { emitEvent: false });
      this.selected_linked_user.set(null);
      name_control?.enable({ emitEvent: false });
      surnames_control?.enable({ emitEvent: false });
      email_control?.enable({ emitEvent: false });
      name_control?.setValidators([Validators.required]);
      surnames_control?.setValidators([Validators.required]);
      email_control?.setValidators([Validators.required, Validators.email]);
      this.form.patchValue({ user_search: '' }, { emitEvent: false });
    }

    user_id_control?.updateValueAndValidity({ emitEvent: false });
    name_control?.updateValueAndValidity({ emitEvent: false });
    surnames_control?.updateValueAndValidity({ emitEvent: false });
    email_control?.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity();
    this.refreshCanSave();
  }

  private applyLinkedUserSelection(user: User): void {
    this.selected_linked_user.set(user);
    this.form.patchValue({
      user_id: user.id,
      user_search: this.formatUserLabel(user),
    });
    this.refreshCanSave();
  }

  private refreshCanSave(): void {
    const is_link_mode = this.user_link_mode() === 'link';
    const can_save = is_link_mode ? this.form.get('user_id')?.value != null : this.form.valid;

    this.can_save.set(can_save);
    this.change_detector_ref.markForCheck();
  }

  private formatUserLabel(user: User): string {
    const full_name = user.full_name ?? `${user.name} ${user.surnames}`.trim();

    return `${full_name} (${user.email})`;
  }

  private resolveUserSearchQuery(search_term: string | User | null): string | null {
    if (search_term === null) {
      return '';
    }

    if (typeof search_term === 'object') {
      return null;
    }

    return search_term.trim();
  }
}
